import { Box, Button, DialogTrigger, Flex, Input, Menu, Stack, Text } from '@chakra-ui/react';
import { CaretDown, UserPlus, X } from '@phosphor-icons/react';
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { useState } from 'react';
import { AccountRole } from '@/types/enums';
import { fetchAPI } from '@/api/fetch';
import { apiInviteTeam } from '@/api/services';
import { Controller, useForm } from 'react-hook-form';
import { Tooltip } from '@/components/ui/tooltip';

interface AccountInvitation {
  email: string;
  role: AccountRole;
}

interface InviteMemberDialogProps {
  onInvite: (accountInvitation: AccountInvitation) => void;
  disabled?: boolean;
}

interface FormValues {
  email: string;
  role: { value: AccountRole };
}

export default function InviteMemberDialog({
  onInvite,
  disabled = false
}: InviteMemberDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors }
  } = useForm<FormValues>({
    defaultValues: {
      email: '',
      role: { value: AccountRole.MEMBER }
    }
  });

  const handleInvite = async (data: FormValues) => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const response = await fetchAPI<void>({
        request: () => apiInviteTeam({ email: data.email, role: data.role.value })
      });

      if (response.success) {
        onInvite({ email: data.email, role: data.role.value });
        reset();
        setIsOpen(false);
      } else {
        if (response.message === 'Email exists') {
          setSubmitError('The email address is already in use');
        } else {
          console.error('Failed to send invitation', response.message);
          setSubmitError('Failed to send invitation. Please try again.');
        }
      }
    } catch {
      console.error('Failed to send invitation');
      setSubmitError('Failed to send invitation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    setSubmitError(null);
    setIsOpen(false);
  };

  const buttonContent = (
    <Button
      borderRadius="8px"
      color="neutrals.0"
      bg="primary.900"
      _hover={{ bg: disabled ? 'primary.900' : 'primary.700' }}
      fontSize="14px"
      aria-label="Open invite member dialog"
      disabled={disabled}
      cursor={disabled ? 'default' : 'pointer'}
      opacity={disabled ? 0.6 : 1}
    >
      <UserPlus size={20} style={{ marginRight: '8px' }} />
      Invite Member
    </Button>
  );

  return (
    <DialogRoot
      size="md"
      open={isOpen}
      onOpenChange={(e) => !disabled && setIsOpen(e.open)}
      closeOnInteractOutside={false}
      placement="center"
      onExitComplete={handleClose}
    >
      <DialogTrigger asChild>
        {disabled ? (
          <Tooltip
            content="Team member limit reached"
            positioning={{ placement: 'bottom' }}
            openDelay={100}
            closeDelay={100}
            contentProps={{
              bg: 'warning.100',
              color: 'warning.700',
              borderRadius: '8px',
              border: '1px solid',
              borderColor: 'neutrals.200',
              boxShadow: 'sm',
              p: '8px',
              fontSize: '14px'
            }}
          >
            {buttonContent}
          </Tooltip>
        ) : (
          buttonContent
        )}
      </DialogTrigger>
      <DialogContent borderRadius="12px" style={{ zIndex: 1000 }}>
        <form onSubmit={handleSubmit(handleInvite)}>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
          </DialogHeader>

          <DialogBody>
            <Stack gap="16px">
              <Field
                label={<Box fontSize="16px">Email Address</Box>}
                invalid={!!errors.email}
                errorText={
                  <Box fontSize="14px" color="error.700">
                    {errors.email?.message}
                  </Box>
                }
              >
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="email"
                      placeholder="Enter email address"
                      fontSize="14px"
                      disabled={isSubmitting}
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? 'email-error' : undefined}
                    />
                  )}
                />
              </Field>
              <Field
                label={<Box fontSize="16px">Role</Box>}
                invalid={!!errors.role}
                errorText={
                  <Box fontSize="14px" color="error.700">
                    {errors.role?.message}
                  </Box>
                }
              >
                <Controller
                  name="role"
                  control={control}
                  rules={{ required: 'Role is required' }}
                  render={({ field }) => (
                    <Menu.Root>
                      <Menu.Trigger asChild>
                        <Button
                          variant="outline"
                          size="lg"
                          width="100%"
                          borderRadius="8px"
                          bg="background.secondary"
                          fontSize="14px"
                          disabled={isSubmitting}
                          borderColor={errors.role ? 'error.500' : 'neutrals.200'}
                        >
                          <Flex justifyContent="space-between" alignItems="center" width="100%">
                            <Box fontSize="16px" color="neutrals.700">
                              {field.value.value.charAt(0).toUpperCase() +
                                field.value.value.slice(1)}
                            </Box>
                            <Box as="span" ml="auto" mr="0">
                              <CaretDown size={16} />
                            </Box>
                          </Flex>
                        </Button>
                      </Menu.Trigger>
                      <Menu.Positioner>
                        <Menu.Content minW="10rem" zIndex={1500}>
                          <Menu.RadioItemGroup
                            value={field.value.value}
                            onValueChange={(value) => field.onChange({ value: value.value })}
                          >
                            <Menu.RadioItem
                              value={AccountRole.MEMBER}
                              fontSize="16px"
                              _hover={{ cursor: 'pointer', bg: 'neutrals.100' }}
                            >
                              Member
                            </Menu.RadioItem>
                            <Menu.RadioItem
                              value={AccountRole.ADMIN}
                              fontSize="16px"
                              _hover={{ cursor: 'pointer', bg: 'neutrals.100' }}
                            >
                              Admin
                            </Menu.RadioItem>
                          </Menu.RadioItemGroup>
                        </Menu.Content>
                      </Menu.Positioner>
                    </Menu.Root>
                  )}
                />
              </Field>
              {submitError && (
                <Text fontSize="14px" color="error.500" textAlign="left" role="alert">
                  {submitError}
                </Text>
              )}
            </Stack>
          </DialogBody>
          <DialogFooter>
            <DialogActionTrigger asChild>
              <Button
                borderColor="primary.900"
                background="background.secondary"
                color="primary.900"
                borderRadius="8px"
                fontSize="16px"
                p="12px 24px"
                _hover={{ bg: 'neutrals.100' }}
                disabled={isSubmitting}
                onClick={handleClose}
                type="button"
              >
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button
              background="primary.900"
              color="neutrals.0"
              borderRadius="8px"
              fontSize="16px"
              type="submit"
              _hover={{ bg: 'primary.700' }}
              p="12px 24px"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </form>
        <DialogCloseTrigger m="12px" disabled={isSubmitting} onClick={handleClose}>
          <X size={32} weight="bold" />
        </DialogCloseTrigger>
      </DialogContent>
    </DialogRoot>
  );
}
